import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  CmsUserSheet,
  CmsUsersTable,
  InviteCmsUserDialog,
  RevokeCmsUserDialog,
  cmsUsersKeys,
  useInviteCmsUser,
  useRevokeCmsUser,
  type CmsRole,
  type CmsUserRow,
} from '~/features/cms-users'
import { meQuery } from '~/features/me'
import { http } from '~/lib/http'
import { logger } from '~/lib/logger'
import { useDebouncedValue } from '~/lib/query/use-debounced-value'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'
import type { AppError } from '~/lib/result'

/**
 * `/admin/cms-users` — CMS Users admin surface (super_admin only,
 * URL-paginated).
 *
 * URL search keys: `page`, `limit`, `search`, `status`.
 */
const cmsUsersSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  search: z.string().optional(),
  status: z.enum(['all', 'active', 'revoked']).default('active'),
})
type CmsUsersSearch = z.infer<typeof cmsUsersSearchSchema>

const URL_DEFAULTS: CmsUsersSearch = {
  page: 1,
  limit: 25,
  status: 'active',
}

export const Route = createFileRoute('/admin/cms-users')({
  validateSearch: cmsUsersSearchSchema,
  component: CmsUsersPage,
})

interface CmsUsersListPayload {
  readonly users?: readonly CmsUserRow[]
  readonly items?: readonly CmsUserRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchCmsUsersPage(
  params: PaginatedParams,
): Promise<PaginatedResult<CmsUserRow>> {
  const query: Record<string, string | number | boolean | undefined> = {
    page: params.page,
    limit: params.limit,
    offset: (params.page - 1) * params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sort) query.sort = params.sort
  const status = params.filters?.status
  if (status === 'active') query.isActive = true
  else if (status === 'revoked') query.isActive = false

  const raw = await http.get<CmsUsersListPayload>(
    '/api/v1/admin/cms-users',
    { query },
  )
  const rows = (raw.users ?? raw.items ?? []) as readonly CmsUserRow[]
  const total = raw.pagination?.total ?? raw.total ?? rows.length
  return toPaginatedResult(rows, total, params, raw.pagination)
}

function CmsUsersPage(): ReactElement {
  const me = useQuery(meQuery())
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(cmsUsersSearchSchema, URL_DEFAULTS)

  const [searchInput, setSearchInput] = useState<string>(search.search ?? '')
  const debouncedSearch = useDebouncedValue(searchInput, 300)
  useEffect(() => {
    const normalized =
      debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim()
    if (normalized !== search.search) {
      setUrl({ search: normalized, page: 1 })
    }
  }, [debouncedSearch, search.search, setUrl])

  const [openUserId, setOpenUserId] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<CmsUserRow | null>(null)
  const [serverErrors, setServerErrors] = useState<{
    invite: string | null
    revoke: string | null
  }>({ invite: null, revoke: null })

  const isSuperAdmin = me.data?.is_super_admin ?? false
  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    search: search.search,
    filters: { status: search.status },
  }
  const listQuery = usePaginatedQuery<CmsUserRow>({
    queryKey: [...cmsUsersKeys.all, 'list-paginated'],
    params,
    fetcher: fetchCmsUsersPage,
    enabled: isSuperAdmin,
  })
  const inviteMutation = useInviteCmsUser()
  const revokeMutation = useRevokeCmsUser()

  const users = useMemo(() => listQuery.data?.data ?? [], [listQuery.data])
  const pagination = listQuery.data?.pagination
  const counts = useMemo(() => deriveCounts(users), [users])
  const roles = useMemo<ReadonlyArray<CmsRole>>(
    () => deriveRoles(users),
    [users],
  )

  const openUser = useMemo<CmsUserRow | null>(
    () =>
      openUserId !== null
        ? (users.find((u) => u.id === openUserId) ?? null)
        : null,
    [openUserId, users],
  )

  const apiError = listQuery.error as AppError | null
  const isPermissionDenied =
    apiError?.code === 'forbidden' || apiError?.code === 'unauthorized'

  if (me.data && !me.data.is_super_admin) {
    return <PermissionDenied />
  }

  if (isPermissionDenied) {
    return <PermissionDenied />
  }

  const closeAll = (): void => {
    setInviteOpen(false)
    setRevokeTarget(null)
    setServerErrors({ invite: null, revoke: null })
  }

  const clearFilters = (): void => {
    setSearchInput('')
    setUrl({ search: undefined, status: 'all', page: 1 })
  }
  const hasFilters = searchInput.length > 0 || search.status !== 'all'

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">CMS Users</h1>
          <p className="ka-page-header-subtitle">
            {pagination?.total ?? counts.total} total · {counts.active} active ·{' '}
            {counts.revoked} revoked
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            onClick={() => setInviteOpen(true)}
          >
            <Plus size={14} aria-hidden="true" /> Invite CMS User
          </button>
        </div>
      </header>

      <div className="ka-filter-strip" role="search">
        <label
          className="ka-filter-search"
          aria-label="Search CMS users by name, email, or role"
        >
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search name · email · role…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>

        <div
          className="ka-filter-group"
          role="group"
          aria-label="Status filter"

        >
          {(['all', 'active', 'revoked'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`ka-detail-chip${search.status === value ? ' is-active' : ''}`}
              aria-pressed={search.status === value}
              onClick={() => setUrl({ status: value, page: 1 })}
            >
              {value === 'all'
                ? 'All'
                : value === 'active'
                  ? 'Active'
                  : 'Revoked'}
            </button>
          ))}
        </div>
      </div>

      <CmsUsersTable
        users={users}
        isLoading={listQuery.isPending}
        error={listQuery.error}
        onRowClick={(user) => setOpenUserId(user.id)}
        onRevoke={(user) => setRevokeTarget(user)}
        onInvite={() => setInviteOpen(true)}
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
      />
      <DataTablePagination
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? users.length}
        isLoading={listQuery.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />

      {openUser ? (
        <CmsUserSheet
          user={openUser}
          onClose={() => setOpenUserId(null)}
          onRequestRevoke={(user) => setRevokeTarget(user)}
        />
      ) : null}

      {inviteOpen ? (
        <InviteCmsUserDialog
          roles={roles}
          isSubmitting={inviteMutation.isPending}
          serverError={serverErrors.invite}
          onClose={closeAll}
          onSubmit={(values) => {
            setServerErrors((prev) => ({ ...prev, invite: null }))
            inviteMutation.mutate(values, {
              onSuccess: () => {
                closeAll()
              },
              onError: (cause) => {
                logger.warn('cms-user invite failed', { cause })
                setServerErrors((prev) => ({
                  ...prev,
                  invite: errorToMessage(cause),
                }))
              },
            })
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeCmsUserDialog
          user={revokeTarget}
          isSubmitting={revokeMutation.isPending}
          serverError={serverErrors.revoke}
          onClose={closeAll}
          onConfirm={({ reason }) => {
            setServerErrors((prev) => ({ ...prev, revoke: null }))
            revokeMutation.mutate(
              {
                userId: revokeTarget.id,
                body: reason ? { reason } : {},
              },
              {
                onSuccess: () => {
                  setOpenUserId(null)
                  closeAll()
                },
                onError: (cause) => {
                  logger.warn('cms-user revoke failed', { cause })
                  setServerErrors((prev) => ({
                    ...prev,
                    revoke: errorToMessage(cause),
                  }))
                },
              },
            )
          }}
        />
      ) : null}
    </div>
  )
}

interface Counts {
  readonly total: number
  readonly active: number
  readonly revoked: number
}

function deriveCounts(users: ReadonlyArray<CmsUserRow>): Counts {
  let active = 0
  let revoked = 0
  for (const user of users) {
    if (user.is_active) active += 1
    else revoked += 1
  }
  return { total: users.length, active, revoked }
}

function deriveRoles(users: ReadonlyArray<CmsUserRow>): ReadonlyArray<CmsRole> {
  const seen = new Map<string, CmsRole>()
  for (const user of users) {
    const role = user.role
    if (role && !seen.has(role.id)) {
      seen.set(role.id, role)
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
}

function errorToMessage(cause: unknown): string {
  if (cause && typeof cause === 'object' && 'message' in cause) {
    const message = (cause as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) return message
  }
  return 'Something went wrong. Please try again.'
}

function PermissionDenied(): ReactElement {
  return (
    <div className="ka-page">
      <div className="ka-card">
        <div className="ka-empty">
          <div className="ka-empty-title">Restricted to super_admin</div>
          <div className="ka-empty-body">
            CMS Users is reserved for platform super admins. If you need
            access, request it from a super admin on your team.
          </div>
        </div>
      </div>
    </div>
  )
}
